import { ApiResponse } from '../types'
import { AxiosClient } from '../utils/clients'

export enum WALLET_TYPE {
    MOMO = 'mobilemoney',
    BANK_CARD = 'bankcard',
    CARD = 'card',
    FINERACT = 'fineract',
    GHANA_GOV = 'ghana.gov',
}

export type Wallet = {
    id: string
    userAccountId: string
    walletType: WALLET_TYPE | string
    balance: number
    accountNumber: string
    cardToken: null | string
    cardExpiry: null | string
    accountName: null | string // Updated to accept null values
    accountCode: null | string
    isDefault: boolean
    cardBinData: null | {
        binCode: string
        cardType: string
        bankName: string
        payMethod: string
        cardLevel: string
        countryName: string
        isoA2: string
        isoA3: string
        countryNumber: string
        backgroundImageUrl: null | string
        bankLogoUrl: null | string // Updated to accept null values
        schemeLogoUrl: null | string
        coverImageUrl: null | string
        bic: string
    }
    providerName: null | string // Updated to accept null values
    userMobileNumber: string
    status: string
    createdAt: string
    updatedAt: null | string
    fineractSavingsId: null | string
    fineractClientId: null | string
    isChallenged: boolean
    challengeReason: null | string
    otpPrefix: string
    requestId: string
    walletDescription?: string
}

export type Bank = {
    bankName: string
    bankCode: string
    serviceId: string
    logoUrl: string
}

export type AllBanksResponse = ApiResponse<Bank[]>
export type VerifyBankAccountNumberReponse = {
    name: string
    sessionId: string
} & Wallet
export type AddBankCardResponse = ApiResponse<{}>

export type WalletResponse = ApiResponse<Wallet[]>
export type AddMomoWalletResponse = ApiResponse<Wallet>

export type GetECGWalletResponse = ApiResponse<Wallet>
export type WithdrawPowerAppBalanceResponse = ApiResponse<{
    transactionId: string
}>

export type MomoProvider = {
    name: string
    logoUrl: string
    alias: string
    alternativeLogoUrl?: string
}

export type MomoProviderResponse = ApiResponse<MomoProvider[]>

export default class WalletRepository {
    private BASE_URL = process.env.NEXT_PUBLIC_WALLETS_BASE_URL
    constructor(private client: AxiosClient) {}

    async getAllBanks() {
        const res = await this.client.get<AllBanksResponse>(`${this.BASE_URL}/wallets/banks`)
        return res.data
    }

    async verifyBankAccountNumber(payload: { selectedWalletId: string; accountNumber: string; bankCode: string }) {
        const res = await this.client.post<ApiResponse<VerifyBankAccountNumberReponse>>(
            `${this.BASE_URL}/wallets/verify-bank-account/${payload.selectedWalletId}`,
            { accountNumber: payload.accountNumber, bankCode: payload.bankCode },
        )
        return res.data
    }

    async registerBankCard(payload: { cardNumber: string; sessionId: string }) {
        const res = await this.client.post<AddBankCardResponse>(`${this.BASE_URL}/wallets/register-card`, payload)
        return res.data
    }

    async addBankCard(payload: { cardNumber: string }) {
        const res = await this.client.post<AddBankCardResponse>(`${this.BASE_URL}/wallets/add-bank-card`, payload)
        return res.data
    }

    async getAllWallets() {
        const res = await this.client.get<WalletResponse>(`${this.BASE_URL}/wallets`)
        return res.data
    }

    async getECGWallet() {
        const res = await this.client.get<GetECGWalletResponse>(`${this.BASE_URL}/wallets/ecg-balance`)
        return res.data
    }

    async withdrawPowerAppBalance(paylaod: { amount: string | number; destinationWalletId: string }) {
        const res = await this.client.post<{
            data: WithdrawPowerAppBalanceResponse
        }>(`${this.BASE_URL}/wallets/withdraw-balance`, paylaod)
        return res.data
    }

    async addMomoWallets(payload: { accountNumber: string; providerName: string }) {
        const res = await this.client.post<{
            data: AddMomoWalletResponse
        }>(`${this.BASE_URL}/wallets/register-momo`, payload)
        return res.data
    }

    async verifyAddMomoWallets(payload: { otpCode: string; otpPrefix: string; requestId: string }) {
        const res = await this.client.post<ApiResponse<null>>(`${this.BASE_URL}/wallets/verify-momo`, payload)
        return res.data
    }

    deleteWallet(walletId: string) {
        return this.client.delete<WalletResponse>(`${this.BASE_URL}/wallets/${walletId}`)
    }

    async getMomoProviders() {
        const res = await this.client.get<MomoProviderResponse>(`${this.BASE_URL}/wallets/momo-providers`)
        return res.data
    }
}
